import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calendar,
  Clock,
  ImageOff,
  Megaphone,
  Newspaper,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { api, assetUrl } from "../lib/api";

function formatDate(value) {
  if (!value) return "Recently published";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function newsDate(item) {
  return item?.published_at || item?.created_at || item?.updated_at;
}

function excerpt(item, limit = 190) {
  const text = String(item?.summary || item?.content || "Read the full official campus update.").replace(/<[^>]+>/g, "").trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
}

function readTime(item) {
  const words = String(item?.content || item?.summary || "").trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 180))} min read`;
}

function categoryLabel(item) {
  return item?.category || item?.type || "Campus Update";
}

function NewsImage({ item, featured = false }) {
  const [broken, setBroken] = useState(false);
  const image = assetUrl(item?.image_url);

  if (!image || broken) {
    return (
      <div className={`relative flex h-full ${featured ? "min-h-[340px]" : "min-h-[220px]"} items-center justify-center overflow-hidden bg-gradient-to-br from-blue-950 via-indigo-950 to-slate-950 p-8 text-center text-white`}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #60a5fa 0, transparent 28%), radial-gradient(circle at 90% 10%, #a855f7 0, transparent 25%), linear-gradient(rgba(255,255,255,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.12) 1px, transparent 1px)", backgroundSize: "auto, auto, 36px 36px, 36px 36px" }} />
        <div className="relative z-10 max-w-sm">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
            <ImageOff className="h-9 w-9 text-blue-100" />
          </div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-200">Official News</p>
          <h3 className="mt-3 text-xl font-black leading-tight">Federal Polytechnic Bida</h3>
          <p className="mt-2 text-sm text-slate-300">Image currently unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={image}
      alt={item?.title || "News image"}
      onError={() => setBroken(true)}
      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
    />
  );
}

function FeaturedStory({ item }) {
  if (!item) return null;
  return (
    <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10">
      <div className="grid lg:grid-cols-[1.08fr_.92fr]">
        <div className="relative min-h-[320px] bg-slate-100 group">
          <NewsImage item={item} featured />
          <div className="absolute left-5 top-5 rounded-full bg-yellow-400 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 shadow-lg">Featured</div>
        </div>
        <div className="flex flex-col justify-center p-7 md:p-10 lg:p-12">
          <div className="mb-5 flex flex-wrap items-center gap-3 text-xs font-black uppercase tracking-wider text-slate-500">
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-blue-700"><Calendar className="h-4 w-4" /> {formatDate(newsDate(item))}</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-slate-700"><Clock className="h-4 w-4" /> {readTime(item)}</span>
          </div>
          <p className="text-sm font-black uppercase tracking-[0.24em] text-blue-700">{categoryLabel(item)}</p>
          <h2 className="mt-3 text-3xl font-black leading-tight tracking-tight text-slate-950 md:text-4xl">{item.title}</h2>
          <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600">{excerpt(item, 260)}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to={`/news/${item.slug || item.id}`} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-700 px-6 py-4 text-sm font-black text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 hover:bg-blue-800">
              Read Full Story <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="#all-news" className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-4 text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
              More Updates
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function NewsCard({ item, compact = false }) {
  return (
    <article className={`group overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-950/10 ${compact ? "grid grid-cols-[116px_1fr]" : ""}`}>
      <div className={`${compact ? "h-full min-h-[132px]" : "h-56"} relative bg-slate-100`}>
        <NewsImage item={item} />
      </div>
      <div className={compact ? "p-4" : "p-6"}>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-black uppercase tracking-wider text-slate-500">
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(newsDate(item))}</span>
          {!compact && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {readTime(item)}</span>}
        </div>
        <h3 className={`${compact ? "mt-2 text-base" : "mt-3 text-xl"} line-clamp-2 font-black leading-tight text-slate-950 group-hover:text-blue-700`}>{item.title}</h3>
        {!compact && <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{excerpt(item)}</p>}
        <Link to={`/news/${item.slug || item.id}`} className="mt-4 inline-flex items-center gap-2 text-sm font-black text-blue-700 hover:text-blue-900">
          Read Story <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

export default function News() {
  const [news, setNews] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadNews() {
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/news?limit=100");
      setNews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching news:", err);
      setError("Published news could not be loaded from the CMS. Please check the backend API.");
      setNews([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNews();
  }, []);

  const filteredNews = useMemo(() => {
    const query = q.trim().toLowerCase();
    const rows = [...news].sort((a, b) => new Date(newsDate(b) || 0) - new Date(newsDate(a) || 0));
    if (!query) return rows;
    return rows.filter((item) => [item.title, item.summary, item.content, item.author, item.category].filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [news, q]);

  const featured = filteredNews[0];
  const latest = filteredNews.slice(1);
  const recentSidebar = filteredNews.slice(0, 5);

  return (
    <main className="min-h-screen bg-[#f5f8fc] text-slate-950">
      <section className="relative isolate overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_18%_20%,rgba(59,130,246,.35),transparent_28%),radial-gradient(circle_at_88%_18%,rgba(168,85,247,.26),transparent_28%),linear-gradient(135deg,#071426,#1d4ed8_50%,#4c1d95)]" />
        <div className="absolute inset-0 -z-10 opacity-[0.09]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.85) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.85) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-18 lg:py-20">
          <div className="grid gap-8 lg:grid-cols-[1fr_380px] lg:items-end">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-blue-100 backdrop-blur"><Newspaper className="h-4 w-4" /> Campus Newsroom</span>
              <h1 className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-tight md:text-6xl">Campus News & Official Updates</h1>
              <p className="mt-5 max-w-3xl text-base leading-8 text-blue-50/90 md:text-lg">Read verified announcements, campus stories, achievements, admissions updates and institutional communication from Federal Polytechnic Bida.</p>
              <div className="mt-8 flex max-w-3xl items-center gap-3 rounded-2xl bg-white p-2 shadow-2xl shadow-blue-950/20">
                <Search className="ml-3 h-5 w-5 shrink-0 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search news, announcements, admissions..." className="min-w-0 flex-1 px-2 py-3 text-sm font-semibold text-slate-800 outline-none" />
                <button onClick={loadNews} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white hover:bg-blue-800"><RefreshCcw className="h-4 w-4" /> Refresh</button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md"><p className="text-xs font-black uppercase tracking-widest text-blue-100">Published</p><strong className="mt-2 block text-3xl font-black">{loading ? "..." : news.length}</strong></div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md"><p className="text-xs font-black uppercase tracking-widest text-blue-100">Showing</p><strong className="mt-2 block text-3xl font-black">{loading ? "..." : filteredNews.length}</strong></div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md"><p className="text-xs font-black uppercase tracking-widest text-blue-100">Source</p><strong className="mt-2 block text-xl font-black">CMS Live</strong></div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14">
        {error ? <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div> : null}

        {loading ? (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="h-[420px] animate-pulse rounded-[2rem] bg-slate-200" />
            <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200" />)}</div>
          </div>
        ) : !filteredNews.length ? (
          <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
            <Megaphone className="mx-auto mb-4 h-12 w-12 text-blue-700" />
            <h2 className="text-2xl font-black text-slate-950">No published news found</h2>
            <p className="mt-2 text-slate-600">Create and publish news from the admin News CMS. Draft news will not appear publicly.</p>
            <button onClick={loadNews} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-black text-white hover:bg-blue-800"><RefreshCcw className="h-4 w-4" /> Refresh</button>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div>
              <FeaturedStory item={featured} />

              <div id="all-news" className="mt-10">
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700"><Sparkles className="h-4 w-4" /> Latest Updates</p>
                    <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">More Campus Stories</h2>
                  </div>
                  <p className="text-sm font-black text-slate-500">{latest.length} more update(s)</p>
                </div>

                {latest.length ? (
                  <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {latest.map((item) => <NewsCard key={item.id || item.slug} item={item} />)}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                    <Newspaper className="mx-auto mb-4 h-10 w-10 text-blue-700" />
                    <h3 className="text-xl font-black text-slate-950">Only one news item is published</h3>
                    <p className="mt-2 text-slate-600">Publish more news from the admin panel and they will appear here automatically.</p>
                  </div>
                )}
              </div>
            </div>

            <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">Newsroom</p>
                <h3 className="mt-2 text-2xl font-black text-slate-950">Official information desk</h3>
                <div className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
                  <p className="flex gap-3"><ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-emerald-600" /> Every item here is pulled from the official CMS and must be published before it appears publicly.</p>
                  <p className="flex gap-3"><Building2 className="mt-1 h-5 w-5 shrink-0 text-blue-600" /> Use the admin dashboard to add announcements, admissions updates and campus achievements.</p>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <h3 className="text-lg font-black text-slate-950">Recent Headlines</h3>
                  <UserRound className="h-5 w-5 text-blue-700" />
                </div>
                <div className="space-y-4">
                  {recentSidebar.map((item) => <NewsCard key={`sidebar-${item.id || item.slug}`} item={item} compact />)}
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </main>
  );
}
